/* Sound effects: real foley samples (Kenney "Casino Audio", CC0 – see
 * public/sfx/LICENSE-kenney-casino-audio.txt) for cards and chips, synthetic
 * WebAudio for UI feedback and jingles. Everything runs through the shared bus
 * in audio.ts (tavern reverb, glue compressor, stereo panning per seat).
 * Until the samples finish loading, the old synthesized sounds fill in. */
import { ac, noiseBuffer, routeWithReverb, sfxAudible, sfxBus } from './audio';

// ---------- sample library ----------
const SAMPLE_SETS = {
  deal: ['card-slide-1.ogg', 'card-slide-2.ogg', 'card-slide-3.ogg', 'card-slide-4.ogg'],
  flip: ['card-place-1.ogg', 'card-place-2.ogg', 'card-place-3.ogg', 'card-place-4.ogg'],
  fold: ['card-shove-1.ogg', 'card-shove-2.ogg', 'card-shove-3.ogg'],
  shuffle: ['card-shuffle.ogg'],
  chip: ['chip-lay-1.ogg', 'chip-lay-2.ogg', 'chip-lay-3.ogg'],
  chips: ['chips-handle-1.ogg', 'chips-handle-2.ogg', 'chips-handle-3.ogg'],
  stack: ['chips-stack-1.ogg', 'chips-stack-2.ogg', 'chips-stack-3.ogg', 'chips-collide-1.ogg'],
} as const;
type SampleSet = keyof typeof SAMPLE_SETS;

const buffers = new Map<SampleSet, AudioBuffer[]>();
let loadStarted = false;

function preloadSamples(): void {
  if (loadStarted) return;
  const c = ac();
  if (!c) return;
  loadStarted = true;
  const base = (import.meta.env.BASE_URL ?? '/') + 'sfx/';
  for (const [set, files] of Object.entries(SAMPLE_SETS) as [SampleSet, readonly string[]][]) {
    for (const file of files) {
      fetch(base + file)
        .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
        .then((buf) => c.decodeAudioData(buf))
        .then((decoded) => {
          const list = buffers.get(set) ?? [];
          list.push(decoded);
          buffers.set(set, list);
        })
        .catch(() => { /* missing/undecodable sample -> synthetic fallback stays */ });
    }
  }
}

interface PlayOpts {
  vol?: number;
  pan?: number;
  wet?: number;
  delay?: number;
  rate?: number;
}

function out(c: AudioContext, pan: number, wet: number): AudioNode {
  const bus = sfxBus()!;
  if (pan !== 0) {
    const p = c.createStereoPanner();
    p.pan.value = Math.max(-1, Math.min(1, pan));
    routeWithReverb(p, bus, wet);
    return p;
  }
  const g = c.createGain();
  routeWithReverb(g, bus, wet);
  return g;
}

/** Plays a random variant of a sample set; false when not loaded yet. */
function sample(set: SampleSet, opts: PlayOpts = {}): boolean {
  const c = ac();
  const list = buffers.get(set);
  if (!c || !list || !list.length) return false;
  const s = c.createBufferSource();
  s.buffer = list[Math.floor(Math.random() * list.length)]!;
  // slight random pitch keeps repeated sounds organic
  s.playbackRate.value = opts.rate ?? 0.94 + Math.random() * 0.12;
  const g = c.createGain();
  g.gain.value = opts.vol ?? 1;
  s.connect(g);
  g.connect(out(c, opts.pan ?? 0, opts.wet ?? 0.12));
  s.start(c.currentTime + (opts.delay ?? 0));
  return true;
}

// ---------- synthetic building blocks (UI sounds, jingles, fallbacks) ----------
interface ToneOpts {
  type?: OscillatorType;
  delay?: number;
  slideTo?: number;
  pan?: number;
  wet?: number;
  attack?: number;
}

function tone(freq: number, dur: number, vol: number, opts: ToneOpts = {}) {
  const c = ac();
  if (!c) return;
  const t = c.currentTime + (opts.delay ?? 0);
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = opts.type ?? 'sine';
  o.frequency.setValueAtTime(freq, t);
  if (opts.slideTo) o.frequency.exponentialRampToValueAtTime(opts.slideTo, t + dur);
  const attack = opts.attack ?? 0.005;
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(vol, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g);
  g.connect(out(c, opts.pan ?? 0, opts.wet ?? 0.12));
  o.start(t);
  o.stop(t + dur + 0.05);
}

function noise(dur: number, vol: number, freq: number, q = 1, delay = 0, pan = 0, wet = 0.1) {
  const c = ac();
  const buf = noiseBuffer();
  if (!c || !buf) return;
  const t = c.currentTime + delay;
  const s = c.createBufferSource();
  s.buffer = buf;
  const f = c.createBiquadFilter();
  f.type = 'bandpass';
  f.frequency.value = freq;
  f.Q.value = q;
  const g = c.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(vol, t + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  s.connect(f);
  f.connect(g);
  g.connect(out(c, pan, wet));
  s.start(t);
  s.stop(t + dur + 0.05);
}

const jitter = (base: number, cents: number) => base * Math.pow(2, ((Math.random() * 2 - 1) * cents) / 1200);

// ---------- public API ----------
export const SFX = {
  unlock() {
    ac();
    preloadSamples();
  },
  /** card being dealt to a seat */
  deal(delay = 0, pan = 0) {
    if (!sfxAudible()) return;
    if (sample('deal', { vol: 0.9, pan, delay })) return;
    noise(0.09, 0.35, 2600, 0.8, delay, pan, 0.14);
    noise(0.05, 0.15, 5200, 2, delay + 0.02, pan, 0.1);
  },
  /** card turning face-up */
  flip(delay = 0, pan = 0) {
    if (!sfxAudible()) return;
    if (sample('flip', { vol: 0.8, pan, delay })) return;
    noise(0.06, 0.3, 4200, 1.5, delay, pan, 0.12);
    tone(jitter(1400, 30), 0.05, 0.05, { type: 'triangle', delay, slideTo: 900, pan });
  },
  /** queue started: soft shuffle plus a curious rising two-note motif */
  queueStart() {
    if (!sfxAudible()) return;
    sample('shuffle', { vol: 0.5, wet: 0.12 });
    tone(392, 0.22, 0.11, { type: 'triangle', delay: 0.08, wet: 0.16 });
    tone(587, 0.32, 0.11, { type: 'triangle', delay: 0.26, wet: 0.16 });
  },
  /** a seat in the queue filled up */
  queueSeat() {
    if (!sfxAudible()) return;
    if (sample('deal', { vol: 0.7 })) return;
    noise(0.07, 0.3, 2800, 0.9, 0, 0, 0.1);
  },
  /** table found: short bright call, distinct from the win fanfare */
  matchFound() {
    if (!sfxAudible()) return;
    sample('stack', { vol: 0.5, wet: 0.14 });
    [523, 659, 784].forEach((f, i) => tone(f, 0.26, 0.13, { type: 'triangle', delay: i * 0.09, wet: 0.18 }));
    tone(1568, 0.3, 0.05, { delay: 0.27, wet: 0.18 });
  },
  /** shuffle before a new hand */
  shuffle(pan = 0) {
    if (!sfxAudible()) return;
    if (sample('shuffle', { vol: 0.7, pan, wet: 0.16 })) return;
    for (let i = 0; i < 5; i++) noise(0.06, 0.2, 3000 + Math.random() * 1500, 1, i * 0.06, pan, 0.12);
  },
  /** chips hitting the felt; bigger bets rattle more */
  chip(amount: number, pan = 0) {
    if (!sfxAudible()) return;
    const set: SampleSet = amount >= 200 ? 'chips' : 'chip';
    if (sample(set, { vol: 0.9, pan, wet: 0.16 })) {
      if (amount >= 600) sample('chip', { vol: 0.6, pan, delay: 0.09, wet: 0.16 });
      return;
    }
    const n = Math.min(6, 1 + Math.floor(Math.log10(Math.max(1, amount))));
    for (let i = 0; i < n; i++) {
      const d = i * 0.045 + Math.random() * 0.01;
      tone(jitter(3200, 60) + Math.random() * 900, 0.07, 0.06, { type: 'square', delay: d, pan, wet: 0.16 });
      tone(jitter(5400, 60) + Math.random() * 800, 0.05, 0.05, { delay: d, pan, wet: 0.16 });
      noise(0.03, 0.2, 7000, 3, d, pan, 0.14);
    }
  },
  /** knuckle knock for a check (kept synthetic – reads clearly) */
  check(pan = 0) {
    if (!sfxAudible()) return;
    tone(160, 0.09, 0.4, { slideTo: 90, pan, wet: 0.2 });
    noise(0.04, 0.25, 900, 1, 0, pan, 0.16);
    tone(150, 0.08, 0.3, { delay: 0.13, slideTo: 85, pan, wet: 0.2 });
    noise(0.04, 0.2, 900, 1, 0.13, pan, 0.16);
  },
  /** cards shoved away */
  fold(pan = 0) {
    if (!sfxAudible()) return;
    if (sample('fold', { vol: 0.8, pan, wet: 0.18 })) return;
    noise(0.22, 0.18, 1500, 0.6, 0, pan, 0.18);
  },
  click() {
    if (!sfxAudible()) return;
    tone(1800, 0.03, 0.04, { type: 'square', wet: 0.05 });
    noise(0.02, 0.15, 4000, 2, 0, 0, 0.05);
  },
  emote(pan = 0) {
    if (!sfxAudible()) return;
    tone(620, 0.09, 0.2, { slideTo: 980, pan, wet: 0.2 });
    tone(1240, 0.12, 0.12, { delay: 0.07, pan, wet: 0.2 });
  },
  win(big: boolean) {
    if (!sfxAudible()) return;
    sample('stack', { vol: 0.8, wet: 0.2 });
    const seq = big ? [523, 659, 784, 1047, 1319] : [523, 659, 784, 1047];
    seq.forEach((f, i) => {
      tone(f, 0.35, 0.18, { type: 'triangle', delay: i * 0.09, wet: 0.28 });
      tone(f * 2, 0.25, 0.06, { delay: i * 0.09, wet: 0.28 });
    });
  },
  lose() {
    if (!sfxAudible()) return;
    tone(330, 0.35, 0.12, { type: 'triangle', slideTo: 262, wet: 0.24 });
    tone(262, 0.45, 0.1, { type: 'triangle', delay: 0.25, slideTo: 196, wet: 0.24 });
  },
  levelup() {
    if (!sfxAudible()) return;
    [392, 523, 659, 784, 1047].forEach((f, i) => tone(f, 0.4, 0.16, { type: 'triangle', delay: i * 0.11, wet: 0.3 }));
  },
  /** all-in: the whole stack slides in, plus a low dramatic sweep */
  allin(pan = 0) {
    if (!sfxAudible()) return;
    const hadSample = sample('stack', { vol: 1, pan, wet: 0.2 });
    tone(110, 0.5, 0.12, { type: 'sawtooth', slideTo: 55, pan, wet: 0.2 });
    if (hadSample) {
      sample('chips', { vol: 0.6, pan, delay: 0.12, wet: 0.2 });
      return;
    }
    noise(0.5, 0.25, 600, 0.7, 0, pan, 0.2);
    for (let i = 0; i < 8; i++) {
      const d = 0.05 + i * 0.04;
      tone(3000 + Math.random() * 1500, 0.07, 0.05, { type: 'square', delay: d, pan, wet: 0.16 });
    }
  },
};
